import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DEFAULT_OUTCOMES, OUTCOMES, OUTCOME_META, TYPE_COLOR, nextRunText, outcomeChip, outcomePill } from "./outcomes";
import { fromUtc } from "./DetailParts";

const STORAGE_KEY = "movesDashboard_board";
const PAST_DAYS = 90;
const FUTURE_DAYS = 60;

const NO_PROPERTY = "No property linked";

const ROW_COLUMNS = {
  xs: "4px 14px 34px 66px minmax(150px, 1.3fr) 84px minmax(190px, 2fr) 72px 20px",
  lg: "4px 14px 36px 74px minmax(150px, 1.1fr) 92px minmax(150px, 1fr) minmax(220px, 2.1fr) 84px 20px",
};
const NO_PROPERTY_KEY = "__none__";

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

// The last board loaded this session. Coming back from a move shows it at once while a fresh one loads.
// lastLoad numbers each load, so an older load still filling in previews can't overwrite a newer board.
let lastBoard = null;
let lastLoad = 0;

// The board comes back with queued moves' energy estimates (billPreviewPending); the exact final bills, which take
// the gateway a dozen service calls each, are asked for once the board is on screen.
const PREVIEW_BATCH = 100;

function applyPreviews(board, previews) {
  return {
    ...board,
    moves: board.moves.map((m) => {
      if (!m.billPreviewPending || !(m.workTableUuid in previews)) return m;
      const preview = previews[m.workTableUuid];
      if (!preview || preview.error) return { ...m, billPreviewPending: false, billPreviewError: preview ? preview.error : undefined };
      return {
        ...m,
        billPreviewPending: false,
        billPreviewFilled: true,
        bill: preview.bill,
        billLines: preview.billLines,
        reason: m.outcome === "ready" ? `Final bill: ${preview.description}` : m.reason,
      };
    }),
  };
}

// While a refreshed board's previews load, its moves keep the bills the previous board had already filled in, so
// amounts don't drop back to the estimate and jump again.
function keepFilledPreviews(board, previous) {
  if (!previous) return board;
  const filled = new Map(previous.moves.filter((m) => m.billPreviewFilled).map((m) => [m.uuid, m]));
  return {
    ...board,
    moves: board.moves.map((m) => {
      const before = filled.get(m.uuid);
      if (!m.billPreviewPending || !before || before.workTableUuid !== m.workTableUuid) return m;
      return { ...m, bill: before.bill, billLines: before.billLines, reason: m.outcome === "ready" && before.outcome === "ready" ? before.reason : m.reason };
    }),
  };
}

async function loadPreviews(board, show) {
  const pending = [...new Set(board.moves.filter((m) => m.billPreviewPending).map((m) => m.workTableUuid))];
  for (let i = 0; i < pending.length; i += PREVIEW_BATCH) {
    const batch = pending.slice(i, i + PREVIEW_BATCH);
    let previews;
    try {
      previews = (await axios.post("/moves-dashboard/bill-previews", { workTableUuids: batch })).data.previews || {};
    } catch (err) {
      const message = err?.response?.data?.message || "Couldn't preview the bill.";
      previews = Object.fromEntries(batch.map((uuid) => [uuid, { error: message }]));
    }
    board = applyPreviews(board, previews);
    show(board);
  }
}

const shortDate = (date) => moment(date, "YYYY-MM-DD").format("MMM D");
const rank = (move) => OUTCOMES.indexOf(move.outcome);

function relativeDay(date, today) {
  const diff = moment(date, "YYYY-MM-DD").diff(moment(today, "YYYY-MM-DD"), "days");
  if (diff === 0) return "today";
  return diff < 0 ? `${-diff}d ago` : `in ${diff}d`;
}

function createdTitle(move) {
  const created = fromUtc(move.created);
  const when = created ? created.format("MMM D, YYYY h:mm A") : "an unknown date";
  return `Created ${when}${move.createdByName ? ` by ${move.createdByName}` : ""}${move.source ? ` (${move.source})` : ""}`;
}

function billText(move) {
  if (move.billBlocked) return "No bill";
  if (move.bill == null) return "—";
  const amount = Number(move.bill).toLocaleString("en-US", { style: "currency", currency: "USD" });
  return move.billEstimated ? `~${amount}` : amount;
}

function countBy(moves) {
  const counts = {};
  moves.forEach((m) => { counts[m.outcome] = (counts[m.outcome] || 0) + 1; });
  return counts;
}

// Open moves on the same unit stay together (move-out first) so dependencies read top to bottom.
function orderIntoBlocks(moves) {
  const byUnit = new Map();
  moves.forEach((m) => {
    if (m.outcome === "processed" || !m.unitUuid) return;
    if (!byUnit.has(m.unitUuid)) byUnit.set(m.unitUuid, []);
    byUnit.get(m.unitUuid).push(m);
  });
  const seen = new Set();
  const blocks = [];
  moves.forEach((m) => {
    if (seen.has(m.uuid)) return;
    const unitMoves = m.unitUuid && m.outcome !== "processed" ? byUnit.get(m.unitUuid) : null;
    if (unitMoves && unitMoves.length > 1) {
      const block = unitMoves.slice().sort((a, b) => (a.kind === "out" ? 0 : 1) - (b.kind === "out" ? 0 : 1) || a.moveDate.localeCompare(b.moveDate));
      block.forEach((x) => seen.add(x.uuid));
      blocks.push(block);
    } else {
      seen.add(m.uuid);
      blocks.push([m]);
    }
  });
  const firstDate = (block) => block.reduce((min, x) => (x.moveDate < min ? x.moveDate : min), block[0].moveDate);
  blocks.sort((a, b) => Math.min(...a.map(rank)) - Math.min(...b.map(rank)) || firstDate(a).localeCompare(firstDate(b)));
  return blocks;
}

function useElementWidth(fallback) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

function Timeline({ moves, startDate, endDate, today, nextRun, range, setRange }) {
  const [ref, measured] = useElementWidth(900);
  const width = Math.max(400, measured);
  const height = 150;
  const padL = 34;
  const padR = 8;
  const axisY = 76;
  const origin = moment(startDate, "YYYY-MM-DD");
  const days = moment(endDate, "YYYY-MM-DD").diff(origin, "days") + 1;
  const dayWidth = (width - padL - padR) / days;
  const indexOf = (date) => moment(date, "YYYY-MM-DD").diff(origin, "days");
  const dateOf = (index) => moment(origin).add(index, "days").format("YYYY-MM-DD");
  const x = (index) => padL + index * dayWidth;
  const todayIndex = indexOf(today);
  // The next run (2 AM Eastern, Tuesday through Friday) takes the moves dated through the day before it.
  const runIndex = nextRun ? indexOf(nextRun.takesThroughDate) + 1 : todayIndex;
  const drag = useRef(null);

  const bins = new Map();
  moves.forEach((m) => {
    const index = indexOf(m.moveDate);
    if (index < 0 || index >= days) return;
    const key = `${index}|${m.kind}`;
    if (!bins.has(key)) bins.set(key, {});
    const bin = bins.get(key);
    bin[m.outcome] = (bin[m.outcome] || 0) + 1;
  });
  let max = 1;
  bins.forEach((bin) => { max = Math.max(max, Object.values(bin).reduce((a, b) => a + b, 0)); });
  const unit = Math.min(13, 50 / max);

  useEffect(() => {
    const dayAt = (clientX) => {
      const rect = ref.current.getBoundingClientRect();
      return Math.max(0, Math.min(days - 1, Math.floor((clientX - rect.left - padL) / dayWidth)));
    };
    const onMove = (event) => {
      if (!drag.current) return;
      const index = dayAt(event.clientX);
      if (index === drag.current.b) return;
      drag.current.b = index;
      drag.current.moved = true;
      const [a, b] = [drag.current.a, index].sort((p, q) => p - q);
      setRange([dateOf(a), dateOf(b)]);
    };
    const onUp = () => {
      if (!drag.current) return;
      if (!drag.current.moved) {
        const date = dateOf(drag.current.a);
        setRange((prev) => (prev && prev[0] === date && prev[1] === date ? null : [date, date]));
      }
      drag.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  });

  const onMouseDown = (event) => {
    event.preventDefault();
    const rect = ref.current.getBoundingClientRect();
    const index = Math.max(0, Math.min(days - 1, Math.floor((event.clientX - rect.left - padL) / dayWidth)));
    drag.current = { a: index, b: index, moved: false };
  };

  const ticks = [];
  const tickEvery = Math.max(1, Math.ceil(56 / (dayWidth * 7)));
  let weekCount = 0;
  for (let i = 0; i < days; i++) {
    if (moment(origin).add(i, "days").isoWeekday() !== 1) continue;
    if (weekCount++ % tickEvery === 0 && Math.abs(i - todayIndex) > 2) ticks.push(i);
  }

  return (
    <svg
      ref={ref}
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: "block", cursor: "crosshair", userSelect: "none" }}
      onMouseDown={onMouseDown}
      onDoubleClick={() => setRange(null)}
      role="img"
      aria-label="Open moves per day. Move-ins above the line, move-outs below, colored by what happens next."
    >
      <rect x={padL} y={20} width={Math.max(0, x(runIndex) - padL)} height={height - 46} fill="#f6f7f9" />
      <text x={padL + 6} y={14} fontSize={11} fill="#8b929b">{nextRunText(nextRun) === "tonight" ? "In tonight's run" : "In the next run"}</text>
      {range ? (
        <rect
          x={x(indexOf(range[0]))}
          y={18}
          width={Math.max(dayWidth, x(indexOf(range[1]) + 1) - x(indexOf(range[0])))}
          height={height - 42}
          fill="rgba(23, 105, 196, 0.08)"
          stroke="rgba(23, 105, 196, 0.55)"
        />
      ) : null}
      <line x1={padL} x2={width - padR} y1={axisY} y2={axisY} stroke="#c9ced4" />
      <text x={2} y={axisY - 20} fontSize={11} fill="#8b929b">In</text>
      <text x={2} y={axisY + 28} fontSize={11} fill="#8b929b">Out</text>
      {[...bins.entries()].map(([key, bin]) => {
        const [indexText, kind] = key.split("|");
        const index = Number(indexText);
        let stacked = 0;
        return OUTCOMES.filter((o) => bin[o]).map((o) => {
          const h = bin[o] * unit;
          const y = kind === "in" ? axisY - 1 - stacked - h : axisY + 1 + stacked;
          stacked += h;
          return (
            <rect key={`${key}|${o}`} x={x(index) + 0.5} y={y} width={Math.max(2, dayWidth - 1)} height={h} rx={1.5} fill={OUTCOME_META[o].color}>
              <title>{`${shortDate(dateOf(index))} · ${bin[o]} move-${kind}${bin[o] > 1 ? "s" : ""} · ${outcomeChip(o, nextRun).toLowerCase()}`}</title>
            </rect>
          );
        });
      })}
      <line x1={x(runIndex)} x2={x(runIndex)} y1={18} y2={height - 24} stroke="#2f2f2f" strokeDasharray="3 3" />
      <text x={x(runIndex) + 5} y={14} fontSize={11} fontWeight={500} fill="#3d434a">
        {nextRun ? `${nextRunText(nextRun) === "tonight" ? "Tonight's run" : "Next run"} · ${shortDate(nextRun.date)}, 2 AM` : "Today · batch runs tonight"}
      </text>
      {ticks.map((i) => (
        <g key={i}>
          <line x1={x(i)} x2={x(i)} y1={height - 24} y2={height - 20} stroke="#c9ced4" />
          <text x={x(i)} y={height - 7} fontSize={11} textAnchor="middle" fill="#8b929b">{shortDate(dateOf(i))}</text>
        </g>
      ))}
    </svg>
  );
}

// The moves on screen, with the board's filters applied, as a CSV file.
function exportCsv(moves, today) {
  const columns = [
    ["Type", (m) => (m.kind === "in" ? "Move-in" : "Move-out")],
    ["Name", (m) => m.name],
    ["Move date", (m) => m.moveDate],
    ["Property", (m) => m.propertyName],
    ["Unit", (m) => m.unitAddress],
    ["Account #", (m) => m.accountNum],
    ["Status", (m) => m.label],
    ["Why", (m) => m.reason],
    ["Hand-off", (m) => m.handoff],
    ["Final bill", (m) => (m.bill != null ? Number(m.bill).toFixed(2) : "")],
    ["Source", (m) => m.source],
    ["Created by", (m) => m.createdByName],
    ["Created", (m) => m.created],
    ["Email", (m) => m.email || m.accountEmail],
  ];
  const cell = (value) => {
    const text = value == null ? "" : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [columns.map(([title]) => title).join(",")]
    .concat(moves.map((m) => columns.map(([, get]) => cell(get(m))).join(",")));
  const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `moves-${today}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function MiniTimeline({ moves, startDate, endDate, today }) {
  const width = 200;
  const pad = 6;
  const first = moment(startDate, "YYYY-MM-DD");
  const span = moment(endDate, "YYYY-MM-DD").diff(first, "days") + 1;
  const x = (date) => pad + (moment(date, "YYYY-MM-DD").diff(first, "days") + 0.5) * (width - 2 * pad) / span;
  const inside = (date) => date >= startDate && date <= endDate;
  return (
    <Box component="svg" viewBox={`0 0 ${width} 30`} aria-hidden="true" sx={{ flex: "0 0 auto", width: { xs: 150, lg: 200 }, height: 30, display: { xs: "none", sm: "block" } }}>
      <line x1={pad} x2={width - pad} y1={15} y2={15} stroke="#d5d9de" />
      {inside(today) ? <line x1={x(today)} x2={x(today)} y1={1} y2={29} stroke="#5f6670" strokeDasharray="2 2" /> : null}
      {moves
        .filter((m) => inside(m.moveDate))
        .sort((a, b) => rank(b) - rank(a))
        .map((m) => {
          const cx = x(m.moveDate);
          const d = m.kind === "in" ? `M${cx - 5} 13 L${cx + 5} 13 L${cx} 3 Z` : `M${cx - 5} 17 L${cx + 5} 17 L${cx} 27 Z`;
          return <path key={m.uuid} d={d} fill={OUTCOME_META[m.outcome].color} stroke="#fff" strokeWidth={0.8} />;
        })}
    </Box>
  );
}

function MoveRow({ move, position, pairProblem, showProperty, today, onOpen }) {
  const meta = OUTCOME_META[move.outcome];
  const attention = move.outcome === "attention";
  const bracket = pairProblem ? "#e3a6a6" : "#c3c8cf";
  const gutterLine = position === "top"
    ? { top: "50%", bottom: 0 }
    : position === "bottom"
      ? { top: 0, height: "50%" }
      : position === "middle" ? { top: 0, bottom: 0 } : null;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(move)}
      onKeyDown={(event) => { if (event.key === "Enter") onOpen(move); }}
      sx={{
        display: "grid",
        gridTemplateColumns: ROW_COLUMNS,
        columnGap: 1.25,
        alignItems: "center",
        minHeight: 44,
        py: 0.75,
        pr: 1.5,
        borderTop: 1,
        borderColor: "divider",
        cursor: "pointer",
        "&:first-of-type": { borderTop: 0 },
        "&:hover": { bgcolor: "action.hover" },
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
      }}
    >
      <Box sx={{ alignSelf: "stretch", my: -0.75, bgcolor: meta.color }} />
      <Box sx={{ alignSelf: "stretch", my: -0.75, position: "relative" }}>
        {gutterLine ? (
          <>
            <Box sx={{ position: "absolute", left: 6, borderLeft: `2px solid ${bracket}`, ...gutterLine }} />
            <Box sx={{ position: "absolute", left: 6, top: "calc(50% - 1px)", width: 8, borderTop: `2px solid ${bracket}` }} />
          </>
        ) : null}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: TYPE_COLOR[move.kind] }}>
        {move.kind === "in" ? "IN" : "OUT"}
      </Typography>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{shortDate(move.moveDate)}</Typography>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{relativeDay(move.moveDate, today)}</Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap component="div" sx={{ fontSize: 14, fontWeight: 500 }}>
          {move.name || "—"}
          {move.accountType ? <Chip size="small" label={move.accountType} sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} /> : null}
        </Typography>
        <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary" }}>
          {showProperty ? `${move.propertyName || NO_PROPERTY} · ` : ""}
          {move.unitAddress || "No unit linked"}
          <Box component="span" sx={{ display: { xs: "inline", lg: "none" } }}> · {move.handoff}</Box>
        </Typography>
      </Box>
      <Box title={createdTitle(move)} sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: move.source === "PM" ? 500 : 400 }}>{move.source || "—"}</Typography>
        <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary" }}>
          {fromUtc(move.created) ? `Created ${fromUtc(move.created).format("MMM D")}` : "—"}
        </Typography>
      </Box>
      <Typography noWrap title={move.handoff} sx={{ display: { xs: "none", lg: "block" }, fontSize: 12.5, color: "text.secondary" }}>
        {move.handoff}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: meta.color }}>{move.label}</Typography>
        <Typography
          title={move.reason}
          sx={{
            fontSize: 12,
            color: attention ? "#8a2020" : "text.secondary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {move.reason}
        </Typography>
      </Box>
      <Typography
        title={move.billBlocked || (move.billPreviewPending ? "Energy estimate; working out the exact final bill" : undefined)}
        sx={{ fontSize: 12.5, textAlign: "right", fontVariantNumeric: "tabular-nums", color: move.billBlocked || move.code === "CHARGE_OVER_CAP" ? "#c62828" : "text.secondary", fontWeight: move.billBlocked || move.code === "CHARGE_OVER_CAP" ? 500 : 400 }}
      >
        {billText(move)}
      </Typography>
      <ChevronRightIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
    </Box>
  );
}

// By move date, a unit's move-out and the move-in after it sit together and are bracketed. By created date (newest
// first) they don't, so there's no bracket.
function MoveRows({ moves, showProperty, today, onOpen, sort }) {
  if (sort === "created") {
    return [...moves]
      .sort((a, b) => (b.created || "").localeCompare(a.created || ""))
      .map((m) => <MoveRow key={m.uuid} move={m} showProperty={showProperty} today={today} onOpen={onOpen} />);
  }
  return orderIntoBlocks(moves).map((block) => {
    if (block.length === 1) {
      return <MoveRow key={block[0].uuid} move={block[0]} showProperty={showProperty} today={today} onOpen={onOpen} />;
    }
    const pairProblem = block.some((m) => m.outcome === "attention" || m.outcome === "hold");
    return block.map((m, i) => (
      <MoveRow
        key={m.uuid}
        move={m}
        position={i === 0 ? "top" : i === block.length - 1 ? "bottom" : "middle"}
        pairProblem={pairProblem}
        showProperty={showProperty}
        today={today}
        onOpen={onOpen}
      />
    ));
  });
}

function PropertyGroup({ name, pmName, moves, open, onToggle, startDate, endDate, today, nextRun, onOpen, sort }) {
  const counts = countBy(moves);
  return (
    <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden" }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(event) => { if (event.key === "Enter") onToggle(); }}
        sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.75, minHeight: 46, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
      >
        <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }} />
        <Box sx={{ minWidth: 0, flex: "0 1 260px" }}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 500 }}>{name}</Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary" }}>{pmName || "No property manager"}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          {OUTCOMES.filter((o) => counts[o]).map((o) => (
            <Box key={o} component="span" sx={{ fontSize: 11.5, fontWeight: 500, px: 0.875, py: 0.125, borderRadius: 999, bgcolor: OUTCOME_META[o].bg, color: OUTCOME_META[o].color, whiteSpace: "nowrap" }}>
              {counts[o]} {outcomePill(o, nextRun)}
            </Box>
          ))}
        </Box>
        <MiniTimeline moves={moves} startDate={startDate} endDate={endDate} today={today} />
      </Box>
      {open ? (
        <Box sx={{ borderTop: 1, borderColor: "divider" }}>
          <MoveRows moves={moves} today={today} onOpen={onOpen} sort={sort} />
        </Box>
      ) : null}
    </Paper>
  );
}

export default function CombinedBoard() {
  const navigate = useNavigate();
  const saved = useRef(loadState());
  const [data, setData] = useState(() => lastBoard);
  const [loading, setLoading] = useState(() => !lastBoard);
  const [refreshing, setRefreshing] = useState(() => Boolean(lastBoard));
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [outcomes, setOutcomes] = useState(() => saved.current?.outcomes || DEFAULT_OUTCOMES);
  const [range, setRange] = useState(() => saved.current?.range || null);
  const [search, setSearch] = useState(() => saved.current?.search || "");
  const [pm, setPm] = useState(() => saved.current?.pm || "");
  const [property, setProperty] = useState(() => saved.current?.property || "");
  const [groupBy, setGroupBy] = useState(() => saved.current?.groupBy || "property");
  const [sort, setSort] = useState(() => saved.current?.sort || "moveDate");
  const [openGroups, setOpenGroups] = useState(() => saved.current?.openGroups || {});

  useEffect(() => {
    saveState({ outcomes, range, search, pm, property, groupBy, sort, openGroups });
  }, [outcomes, range, search, pm, property, groupBy, sort, openGroups]);

  useEffect(() => {
    let active = true;
    const load = ++lastLoad;
    // The cache follows the latest board even after the page is left, so the next visit starts from it.
    const show = (board) => {
      if (load === lastLoad) lastBoard = board;
      if (active) setData(board);
    };
    axios.get("/moves-dashboard/board", {
      params: {
        startDate: moment().subtract(PAST_DAYS, "days").format("YYYY-MM-DD"),
        endDate: moment().add(FUTURE_DAYS, "days").format("YYYY-MM-DD"),
      },
    })
      .then((response) => {
        const board = keepFilledPreviews(response.data, lastBoard);
        show(board);
        if (active) {
          setLoading(false);
          setRefreshing(false);
          setRefreshError("");
        }
        return loadPreviews(board, show);
      })
      .catch((err) => {
        if (!active) return;
        const message = err?.response?.data?.message || "Could not load moves.";
        if (lastBoard) setRefreshError(`Couldn't refresh the moves, so this is the list from your last visit. ${message}`);
        else setError(message);
        setLoading(false);
        setRefreshing(false);
      });
    return () => { active = false; };
  }, []);

  const today = data?.today || moment().format("YYYY-MM-DD");
  const startDate = data?.startDate || moment().subtract(PAST_DAYS, "days").format("YYYY-MM-DD");
  const endDate = data?.endDate || moment().add(FUTURE_DAYS, "days").format("YYYY-MM-DD");
  const selected = useMemo(() => new Set(outcomes), [outcomes]);

  const pmOptions = useMemo(
    () => [...new Set((data?.moves || []).map((m) => m.pmName).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [data]
  );
  // Limited to the chosen property manager's properties.
  const propertyOptions = useMemo(() => {
    const byKey = new Map();
    (data?.moves || []).forEach((m) => {
      if (pm && m.pmName !== pm) return;
      const key = m.propertyUuid || NO_PROPERTY_KEY;
      if (!byKey.has(key)) byKey.set(key, m.propertyName || NO_PROPERTY);
    });
    return [...byKey.entries()].map(([key, name]) => ({ key, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data, pm]);
  // A saved property with no moves under the current filters would hide every row, so it stops applying.
  const activeProperty = propertyOptions.some((o) => o.key === property) ? property : "";

  const base = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data?.moves || []).filter((m) => {
      if (pm && m.pmName !== pm) return false;
      if (activeProperty && (m.propertyUuid || NO_PROPERTY_KEY) !== activeProperty) return false;
      if (!query) return true;
      return [m.name, m.email, m.accountEmail, m.unitAddress, m.propertyName, m.handoff, m.reason, m.accountNum]
        .some((value) => value != null && String(value).toLowerCase().includes(query));
    });
  }, [data, search, pm, activeProperty]);

  const inRange = (m) => !range || (m.moveDate >= range[0] && m.moveDate <= range[1]);
  const counts = countBy(base.filter(inRange));
  const timelineMoves = base.filter((m) => selected.has(m.outcome));
  const visible = timelineMoves.filter(inRange);

  const groups = useMemo(() => {
    const byProperty = new Map();
    visible.forEach((m) => {
      const name = m.propertyName || NO_PROPERTY;
      if (!byProperty.has(name)) byProperty.set(name, { name, pmName: m.pmName, moves: [] });
      byProperty.get(name).moves.push(m);
    });
    const n = (group, o) => group.counts[o] || 0;
    return [...byProperty.values()]
      .map((group) => ({ ...group, counts: countBy(group.moves) }))
      .sort((a, b) =>
        n(b, "attention") - n(a, "attention")
        || (n(b, "hold") + n(b, "ready")) - (n(a, "hold") + n(a, "ready"))
        || n(b, "tonight") - n(a, "tonight")
        || a.name.localeCompare(b.name));
  }, [visible]);

  const onChipClick = (outcome, event) => {
    if (event.shiftKey || event.metaKey || event.ctrlKey) {
      setOutcomes((prev) => (prev.includes(outcome) ? prev.filter((o) => o !== outcome) : [...prev, outcome]));
      return;
    }
    setOutcomes((prev) => (prev.length === 1 && prev[0] === outcome ? DEFAULT_OUTCOMES : [outcome]));
  };

  const quickRanges = [
    { key: "past", label: "Past due", value: [startDate, moment(today).subtract(1, "day").format("YYYY-MM-DD")] },
    { key: "next7", label: "Next 7 days", value: [today, moment(today).add(6, "days").format("YYYY-MM-DD")] },
    { key: "next30", label: "Next 30 days", value: [today, moment(today).add(29, "days").format("YYYY-MM-DD")] },
    { key: "all", label: "All dates", value: null },
  ];
  const sameRange = (a, b) => (!a && !b) || (a && b && a[0] === b[0] && a[1] === b[1]);
  const customRange = range && !quickRanges.some((q) => sameRange(q.value, range));
  const openMove = (move) => navigate(`/move-ins-outs/${move.kind}/${move.uuid}`);
  const propertyCount = new Set(visible.map((m) => m.propertyName || NO_PROPERTY)).size;
  const rangeLabel = range
    ? (quickRanges.find((q) => sameRange(q.value, range)) || {}).label
      || (range[0] === range[1] ? shortDate(range[0]) : `${shortDate(range[0])} – ${shortDate(range[1])}`)
    : null;

  // Filters are remembered for the tab, so a narrowed view has to say so, or the board looks like it lost data.
  const defaultOutcomes = outcomes.length === DEFAULT_OUTCOMES.length && DEFAULT_OUTCOMES.every((o) => selected.has(o));
  const propertyLabel = activeProperty ? (propertyOptions.find((o) => o.key === activeProperty) || {}).name : null;
  const activeFilters = [
    range ? { key: "range", label: `Dates: ${rangeLabel}`, clear: () => setRange(null) } : null,
    !defaultOutcomes ? {
      key: "outcomes",
      label: OUTCOMES.filter((o) => selected.has(o)).map((o) => outcomeChip(o, data?.nextRun)).join(" + ") + (outcomes.length === 1 ? " only" : ""),
      clear: () => setOutcomes(DEFAULT_OUTCOMES),
    } : null,
    search.trim() ? { key: "search", label: `Search: “${search.trim()}”`, clear: () => setSearch("") } : null,
    pm ? { key: "pm", label: pm, clear: () => setPm("") } : null,
    propertyLabel ? { key: "property", label: propertyLabel, clear: () => setProperty("") } : null,
    sort === "created" ? { key: "sort", label: "Sorted by created date", clear: () => setSort("moveDate") } : null,
  ].filter(Boolean);
  const clearFilters = () => {
    setRange(null);
    setOutcomes(DEFAULT_OUTCOMES);
    setSearch("");
    setPm("");
    setProperty("");
    setSort("moveDate");
  };
  const totalMoves = (data?.moves || []).length;

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {data?.truncated ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Showing the first 2,000 move-ins and move-outs. Narrow the dates to see the rest.</Alert>
      ) : null}
      {refreshError ? <Alert severity="warning" sx={{ mb: 1.5 }}>{refreshError}</Alert> : null}

      <Paper variant="outlined" sx={{ position: "relative", overflow: "hidden", px: 1.75, pt: 1.5, pb: 1, mb: 1.5 }}>
        {refreshing ? (
          <LinearProgress aria-label="Refreshing moves" sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 2 }} />
        ) : null}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center", mb: 0.75 }}>
          {OUTCOMES.map((o) => {
            const on = selected.has(o);
            const meta = OUTCOME_META[o];
            return (
              <Box
                key={o}
                role="button"
                tabIndex={0}
                aria-pressed={on}
                onClick={(event) => onChipClick(o, event)}
                onKeyDown={(event) => { if (event.key === "Enter") onChipClick(o, event); }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  border: 1,
                  fontSize: 12.5,
                  cursor: "pointer",
                  userSelect: "none",
                  borderColor: on ? meta.color : "divider",
                  bgcolor: on ? meta.bg : "background.paper",
                  color: on ? meta.color : "text.secondary",
                }}
              >
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: meta.color }} />
                {outcomeChip(o, data?.nextRun)}
                <Box component="span" sx={{ fontWeight: 500 }}>{counts[o] || 0}</Box>
              </Box>
            );
          })}
          <Button size="small" onClick={() => setOutcomes(DEFAULT_OUTCOMES)} sx={{ textTransform: "none" }}>All open</Button>
          {data?.deletedSinceYesterday != null ? (
            <Box
              component={RouterLink}
              to={`/move-ins-outs/deleted?startDate=${moment(today, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD")}`}
              sx={{
                ml: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                border: 1,
                borderStyle: "dashed",
                borderColor: data.deletedSinceYesterday ? "#c62828" : "divider",
                fontSize: 12.5,
                color: data.deletedSinceYesterday ? "#c62828" : "text.secondary",
                textDecoration: "none",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              Deleted since yesterday
              <Box component="span" sx={{ fontWeight: 600 }}>{data.deletedSinceYesterday}</Box>
              →
            </Box>
          ) : null}
        </Box>

        <Timeline moves={timelineMoves} startDate={startDate} endDate={endDate} today={today} nextRun={data?.nextRun} range={range} setRange={setRange} />

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, pt: 0.25 }}>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            ▲ move-ins above the line · ▼ move-outs below · Drag to pick dates, double-click to clear
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.25 }}>
            {customRange ? (
              <Chip size="small" label={rangeLabel} onDelete={() => setRange(null)} color="primary" variant="outlined" sx={{ mr: 0.75, height: 24 }} />
            ) : null}
            {quickRanges.map((q) => (
              <Button
                key={q.key}
                size="small"
                variant={sameRange(q.value, range) ? "outlined" : "text"}
                onClick={() => setRange(q.value)}
                sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 1, py: 0.25, color: "text.primary", borderColor: "divider" }}
              >
                {q.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, mb: 1 }}>
        <TextField
          size="small"
          placeholder="Search name, email, unit, property"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 360 }}
        />
        <TextField
          select
          size="small"
          value={pm}
          onChange={(event) => setPm(event.target.value)}
          sx={{ minWidth: 220 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All property managers</MenuItem>
          {pmOptions.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
        </TextField>
        <TextField
          select
          size="small"
          value={activeProperty}
          onChange={(event) => setProperty(event.target.value)}
          sx={{ minWidth: 220, maxWidth: 320 }}
          SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } }}
        >
          <MenuItem value="">All properties</MenuItem>
          {propertyOptions.map((o) => <MenuItem key={o.key} value={o.key}>{o.name}</MenuItem>)}
        </TextField>
        <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(event, next) => { if (next) setGroupBy(next); }}>
          <ToggleButton value="property" sx={{ textTransform: "none", px: 1.5 }}>By property</ToggleButton>
          <ToggleButton value="none" sx={{ textTransform: "none", px: 1.5 }}>All moves</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={sort} onChange={(event, next) => { if (next) setSort(next); }} aria-label="Sort moves">
          <ToggleButton value="moveDate" sx={{ textTransform: "none", px: 1.5 }}>Move date</ToggleButton>
          <ToggleButton value="created" sx={{ textTransform: "none", px: 1.5 }}>Created date</ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={{ ml: "auto", fontSize: 12.5, color: "text.secondary" }}>
          {visible.length} {visible.length === 1 ? "move" : "moves"} · {propertyCount} {propertyCount === 1 ? "property" : "properties"}
        </Typography>
        <Button size="small" variant="outlined" color="inherit" onClick={() => exportCsv(visible, today)} disabled={!visible.length}
          sx={{ textTransform: "none", color: "text.secondary", borderColor: "divider" }}>
          Export CSV
        </Button>
      </Box>

      {activeFilters.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1, px: 1.25, py: 0.75, borderRadius: 1, bgcolor: "#eef4fc", border: "1px solid #cfe0f5" }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 500, mr: 0.25 }}>
            Showing {visible.length} of {totalMoves} moves. Filtered by
          </Typography>
          {activeFilters.map((f) => (
            <Chip key={f.key} size="small" label={f.label} onDelete={f.clear} sx={{ bgcolor: "background.paper", height: 24 }} />
          ))}
          <Button size="small" onClick={clearFilters} sx={{ textTransform: "none", ml: "auto" }}>Clear filters</Button>
        </Box>
      ) : null}

      {visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" sx={{ mb: activeFilters.length ? 1 : 0 }}>No moves match these filters.</Typography>
          {activeFilters.length ? <Button size="small" variant="outlined" onClick={clearFilters} sx={{ textTransform: "none" }}>Clear filters</Button> : null}
        </Paper>
      ) : groupBy === "none" ? (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <MoveRows moves={visible} showProperty today={today} onOpen={openMove} sort={sort} />
        </Paper>
      ) : (
        groups.map((group) => {
          const defaultOpen = (group.counts.attention || 0) + (group.counts.hold || 0) + (group.counts.ready || 0) > 0;
          const open = group.name in openGroups ? openGroups[group.name] : defaultOpen;
          return (
            <PropertyGroup
              key={group.name}
              name={group.name}
              pmName={group.pmName}
              moves={group.moves}
              open={open}
              onToggle={() => setOpenGroups((prev) => ({ ...prev, [group.name]: !open }))}
              startDate={range ? range[0] : startDate}
              endDate={range ? range[1] : endDate}
              today={today}
              nextRun={data?.nextRun}
              onOpen={openMove}
              sort={sort}
            />
          );
        })
      )}
    </Box>
  );
}
